<template>
  <div class="minister-map-page">
    <PageLoader :storage="appLoading" />
    <ServerError v-if="serverError" />

    <div class="minister-map-page__summary">
      <div class="map-summary-card">
        <span class="map-summary-card__label">Reported</span>
        <strong class="map-summary-card__value">{{ formatNumber(summary.reported) }}</strong>
      </div>
      <div class="map-summary-card">
        <span class="map-summary-card__label">Rescued / Resolved</span>
        <strong class="map-summary-card__value">{{ formatNumber(summary.rescued) }}</strong>
      </div>
      <div class="map-summary-card">
        <span class="map-summary-card__label">Released</span>
        <strong class="map-summary-card__value">{{ formatNumber(summary.released) }}</strong>
      </div>
      <div class="map-summary-card">
        <span class="map-summary-card__label">Visible districts</span>
        <strong class="map-summary-card__value">{{ districtRows.length }}</strong>
      </div>
    </div>

    <div class="minister-map-page__canvas">
      <l-map
        ref="ministerMap"
        :zoom="zoom"
        :center="center"
        :options="{ zoomControl: false }"
        class="minister-map"
      >
        <l-tile-layer :url="tileUrl" :attribution="attribution" />
        <l-geo-json
          v-if="keralaGeoJson"
          :geojson="keralaGeoJson"
          :options-style="geoJsonStyle"
        />

        <l-circle-marker
          v-for="marker in visibleMarkers"
          :key="marker.id"
          :lat-lng="marker.latlng"
          :radius="6"
          :color="marker.color"
          :fill-color="marker.color"
          :fill-opacity="0.88"
          :weight="1"
        >
          <l-popup>
            <div class="minister-map-popup">
              <div class="minister-map-popup__title">{{ marker.title }}</div>
              <div><strong>District:</strong> {{ marker.district }}</div>
              <div><strong>Status:</strong> {{ marker.status }}</div>
              <div><strong>Location:</strong> {{ marker.locationName }}</div>
            </div>
          </l-popup>
        </l-circle-marker>
      </l-map>

      <div class="minister-map-page__legend">
        <div class="minister-map-page__legend-title">Legend</div>
        <div v-for="item in legendItems" :key="item.label" class="legend-row">
          <span class="legend-row__dot" :style="{ backgroundColor: item.color }"></span>
          <span>{{ item.label }}</span>
        </div>
      </div>

      <div class="minister-map-page__districts">
        <div class="minister-map-page__district-title">Top districts</div>
        <div
          v-for="district in districtRows.slice(0, 8)"
          :key="district.name"
          class="minister-map-page__district-row"
        >
          <span>{{ district.name }}</span>
          <strong>{{ district.reported }}</strong>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import L from "leaflet";
import { LMap, LTileLayer, LGeoJson, LCircleMarker, LPopup } from "vue2-leaflet";

const RESCUED_STATUSES = new Set(["Captured", "Resolved", "HandOver", "Closed"]);

export default {
  components: {
    LMap,
    LTileLayer,
    LGeoJson,
    LCircleMarker,
    LPopup,
  },
  data() {
    return {
      appLoading: false,
      hasLoadedOnce: false,
      serverError: false,
      refreshHandle: null,
      center: [10.8505, 76.2711],
      zoom: 7,
      tileUrl: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      attribution: "&copy; OpenStreetMap contributors",
      keralaGeoJson: null,
      reports: [],
      summary: {
        reported: 0,
        rescued: 0,
        released: 0,
      },
      districtRows: [],
      districtCounts: {},
      legendItems: [
        { label: "Pending / Assigned", color: "#d88c3a" },
        { label: "Rescued / Resolved", color: "#4e9a50" },
        { label: "Released", color: "#2f6fdd" },
      ],
    };
  },
  computed: {
    visibleMarkers() {
      return this.reports
        .filter((report) => {
          if (!Array.isArray(report.location) || report.location.length < 2) return false;
          const [lng, lat] = report.location;
          // Filter to Kerala bounds: Lat [8.0, 13.0], Lng [74.0, 78.5]
          return lat >= 8.0 && lat <= 13.0 && lng >= 74.0 && lng <= 78.5;
        })
        .map((report) => ({
          id: report._id,
          latlng: [report.location[1], report.location[0]],
          color: this.getMarkerColor(report.status),
          title: report.hwcId || "Conflict Report",
          district: report.district || "Unknown",
          status: report.status || "Pending",
          locationName: report.locationname || report.address || "Not available",
        }));
    },
  },
  mounted() {
    this.loadMapData({ showLoader: true });
    this.refreshHandle = setInterval(() => this.loadMapData({ showLoader: false }), 30000);
  },
  beforeDestroy() {
    if (this.refreshHandle) {
      clearInterval(this.refreshHandle);
    }
  },
  methods: {
    geoJsonStyle(feature) {
      const districtName = this.getDistrictName(feature);
      const count = this.districtCounts[districtName] || 0;

      if (count > 0) {
        return {
          color: "#000000",
          weight: 2,
          opacity: 0.95,
          fillColor: "#e2efe0",
          fillOpacity: 0.45,
        };
      } else {
        return {
          color: "#000000",
          weight: 1,
          opacity: 0.6,
          fillColor: "#f9fcf8",
          fillOpacity: 0.08,
        };
      }
    },
    getDistrictName(feature) {
      if (!feature || !feature.properties) {
        return "";
      }
      const raw = (
        feature.properties.DISTRICT ||
        feature.properties.circleName ||
        feature.properties.name ||
        feature.properties.NAME ||
        feature.properties.district ||
        ""
      );
      return raw.toLowerCase().trim();
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString("en-IN");
    },
    getMarkerColor(status) {
      if (status === "Released") {
        return "#2f6fdd";
      }
      if (RESCUED_STATUSES.has(status)) {
        return "#4e9a50";
      }
      return "#d88c3a";
    },
    async loadMapData({ showLoader = false } = {}) {
      if (showLoader || !this.hasLoadedOnce) {
        this.appLoading = true;
      }

      if (!this.hasLoadedOnce) {
        this.serverError = false;
      }

      try {
        const [stateRes, reportRes] = await Promise.all([
          axios({
            method: "GET",
            url: "/alldata/formap/",
            headers: { "x-auth-token": localStorage.getItem("token") },
          }),
          axios({
            method: "GET",
            url: "/hwc/conflictreports/inmap",
            headers: { "x-auth-token": localStorage.getItem("token") },
          }),
        ]);

        this.keralaGeoJson = stateRes.data && stateRes.data.data ? stateRes.data.data : null;
        this.reports = reportRes.data && reportRes.data.data ? reportRes.data.data : [];
        this.buildSummary();
        this.$nextTick(() => {
          this.fitBounds();
        });
        this.hasLoadedOnce = true;
        this.serverError = false;
      } catch (error) {
        console.log(error);
        if (!this.hasLoadedOnce) {
          this.serverError = true;
        }
      } finally {
        if (showLoader || !this.hasLoadedOnce) {
          this.appLoading = false;
        }
      }
    },
    buildSummary() {
      let rescued = 0;
      let released = 0;
      const districtMap = {};
      const counts = {};

      this.reports.forEach((report) => {
        const districtName = report.district || "Unknown";
        if (!districtMap[districtName]) {
          districtMap[districtName] = { name: districtName, reported: 0 };
        }

        districtMap[districtName].reported += 1;

        const districtKey = districtName.toLowerCase().trim();
        counts[districtKey] = (counts[districtKey] || 0) + 1;

        if (RESCUED_STATUSES.has(report.status)) {
          rescued += 1;
        }

        if (report.status === "Released") {
          released += 1;
        }
      });

      this.summary = {
        reported: this.reports.length,
        rescued,
        released,
      };

      this.districtCounts = counts;
      this.districtRows = Object.values(districtMap)
        .filter((district) => district.name !== "Unknown")
        .sort((a, b) => b.reported - a.reported);
    },
    fitBounds() {
      if (!this.$refs.ministerMap || !this.$refs.ministerMap.mapObject) {
        return;
      }

      const map = this.$refs.ministerMap.mapObject;

      if (this.visibleMarkers.length > 0) {
        const bounds = L.latLngBounds(this.visibleMarkers.map((marker) => marker.latlng));
        map.fitBounds(bounds.pad(0.18));
        return;
      }

      if (this.keralaGeoJson) {
        const geoBounds = L.geoJSON(this.keralaGeoJson).getBounds();
        if (geoBounds.isValid()) {
          map.fitBounds(geoBounds.pad(0.05));
        }
      }
    },
  },
};
</script>

<style scoped>
.minister-map-page {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
  overflow: hidden;
}

.minister-map-page__summary {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
}

.map-summary-card {
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(37, 75, 49, 0.08);
  border-radius: 18px;
  padding: 14px 16px;
  box-shadow: 0 14px 28px rgba(18, 42, 24, 0.07);
}

.map-summary-card__label {
  display: block;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #657865;
  font-weight: 700;
}

.map-summary-card__value {
  display: block;
  margin-top: 10px;
  color: #173824;
  font-size: 28px;
  line-height: 1;
}

.minister-map-page__canvas {
  position: relative;
  flex: 1;
  min-height: 0;
  border-radius: 24px;
  overflow: hidden;
  border: 2px solid #000000;
  box-shadow: 0 20px 34px rgba(18, 42, 24, 0.1);
}

.minister-map {
  width: 100%;
  height: 100%;
}

.minister-map-page__legend,
.minister-map-page__districts {
  position: absolute;
  z-index: 500;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(10px);
  border-radius: 18px;
  box-shadow: 0 18px 30px rgba(18, 42, 24, 0.14);
}

.minister-map-page__legend {
  top: 16px;
  left: 16px;
  padding: 14px 16px;
}

.minister-map-page__districts {
  top: 16px;
  right: 16px;
  width: 220px;
  padding: 14px 16px;
}

.minister-map-page__legend-title,
.minister-map-page__district-title {
  font-size: 13px;
  font-weight: 700;
  color: #224023;
  margin-bottom: 10px;
}

.legend-row,
.minister-map-page__district-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  font-size: 12px;
  color: #536653;
}

.legend-row + .legend-row,
.minister-map-page__district-row + .minister-map-page__district-row {
  margin-top: 8px;
}

.legend-row__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-right: 8px;
}

.minister-map-popup {
  min-width: 160px;
  font-size: 12px;
  line-height: 1.5;
  color: #214021;
}

.minister-map-popup__title {
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 6px;
}

@media (max-width: 1100px) {
  .minister-map-page {
    overflow: auto;
  }

  .minister-map-page__summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .minister-map-page__canvas {
    min-height: 620px;
  }
}
</style>
